/**
 * Groupings (Agrupaciones promocionales) admin — Fase 3 del motor comercial.
 * Curate collections of products / promotions / combos under a named campaign
 * and assign each campaign to a strategic section of /tienda.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, Stack, X, Image as ImageIcon,
  CheckCircle, XCircle, ArrowUp, ArrowDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { uploadPhoto } from "@/lib/uploadPhoto";

const SECTIONS = [
  { v: "hero", label: "Hero (banner principal)" },
  { v: "ofertas_semana", label: "Ofertas de la semana" },
  { v: "remates", label: "Remates" },
  { v: "premium", label: "Premium" },
  { v: "recomendados", label: "Recomendados" },
  { v: "nuevos", label: "Nuevos ingresos" },
  { v: "custom", label: "Custom (por slug)" },
];

const EMPTY = {
  name: "", section: "ofertas_semana", description: "",
  banner_url: "", accent_color: "#002FA7", priority: 0, active: true,
  start_date: "", end_date: "", items: [],
  rotation_strategy: "fixed", visible_limit: 0,
};

export default function Groupings() {
  const [groupings, setGroupings] = useState([]);
  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: gs }, { data: ps }, { data: prs }, { data: cs }] = await Promise.all([
        api.get("/groupings"), api.get("/products"),
        api.get("/promotions"), api.get("/combos"),
      ]);
      setGroupings(gs); setProducts(ps); setPromotions(prs); setCombos(cs);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (g) => {
    setEditingId(g.id);
    setForm({
      name: g.name, section: g.section, description: g.description || "",
      banner_url: g.banner_url || "", accent_color: g.accent_color || "#002FA7",
      priority: g.priority || 0, active: g.active,
      start_date: g.start_date ? g.start_date.slice(0, 10) : "",
      end_date: g.end_date ? g.end_date.slice(0, 10) : "",
      items: (g.items || []).map((i) => ({ kind: i.kind, id: i.id, order: i.order || 0 })),
      rotation_strategy: g.rotation_strategy || "fixed",
      visible_limit: Number(g.visible_limit || 0),
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (form.items.length === 0) return toast.error("Agrega al menos un elemento");
    setSaving(true);
    try {
      const payload = {
        ...form,
        priority: Number(form.priority || 0),
        visible_limit: Number(form.visible_limit || 0),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        items: form.items.map((it, idx) => ({ kind: it.kind, id: it.id, order: idx })),
      };
      if (editingId) await api.patch(`/groupings/${editingId}`, payload);
      else await api.post("/groupings", payload);
      toast.success(editingId ? "Agrupación actualizada" : "Agrupación creada");
      setShowForm(false); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const remove = async (g) => {
    if (!window.confirm(`¿Eliminar "${g.name}"?`)) return;
    try {
      await api.delete(`/groupings/${g.id}`);
      toast.success("Eliminado"); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (g) => {
    try { await api.patch(`/groupings/${g.id}`, { active: !g.active }); await load(); } catch (err) { toast.error(err?.response?.data?.detail); }
  };

  const addItem = (kind, id) => {
    if (form.items.some((i) => i.kind === kind && i.id === id)) return;
    setForm((f) => ({ ...f, items: [...f.items, { kind, id, order: f.items.length }] }));
    setSearch("");
  };
  const removeFormItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const moveItem = (idx, dir) => {
    setForm((f) => {
      const next = [...f.items]; const target = idx + dir;
      if (target < 0 || target >= next.length) return f;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, items: next };
    });
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Máx 8 MB");
    try {
      const url = await uploadPhoto(file, { folder: "alendrapc/groupings", maxSide: 2000, quality: 0.85 });
      setForm((f) => ({ ...f, banner_url: url }));
    } catch (err) { toast.error(err?.message || "No se pudo subir"); }
  };

  const itemLabel = (it) => {
    if (it.kind === "product") {
      const p = products.find((x) => x.id === it.id);
      return p ? `🏷️ ${p.name}` : `🏷️ (eliminado)`;
    }
    if (it.kind === "promotion") {
      const p = promotions.find((x) => x.id === it.id);
      return p ? `🔥 ${p.name}` : `🔥 (eliminado)`;
    }
    if (it.kind === "combo") {
      const p = combos.find((x) => x.id === it.id);
      return p ? `📦 ${p.name}` : `📦 (eliminado)`;
    }
    return it.id;
  };

  const filterText = search.toLowerCase().trim();
  const filteredProducts = filterText ? products.filter((p) => p.name.toLowerCase().includes(filterText)).slice(0, 8) : [];
  const filteredPromotions = filterText ? promotions.filter((p) => p.name.toLowerCase().includes(filterText)).slice(0, 8) : [];
  const filteredCombos = filterText ? combos.filter((c) => c.name.toLowerCase().includes(filterText)).slice(0, 8) : [];

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MOTOR COMERCIAL · FASE 3</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <Stack size={32} weight="duotone" className="text-[#00873D]" />
            Agrupaciones
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">{groupings.length} agrupaciones · {groupings.filter((g) => g.live).length} en vivo en la tienda</p>
        </div>
        <Button onClick={openCreate} className="gap-2 bg-[#00873D] hover:bg-[#006A2D] text-white" data-testid="create-grouping">
          <Plus size={14} weight="bold" /> Nueva agrupación
        </Button>
      </header>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Sección</th>
              <th className="text-left px-4 py-3">Items</th>
              <th className="text-left px-4 py-3">Vigencia</th>
              <th className="text-left px-4 py-3">Prio.</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-16 text-center text-zinc-400">Cargando…</td></tr>}
            {!loading && groupings.length === 0 && <tr><td colSpan={7} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin agrupaciones aún.</td></tr>}
            {!loading && groupings.map((g) => (
              <tr key={g.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`grouping-row-${g.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{g.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">/{g.slug}</div>
                </td>
                <td className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-600 font-mono">{SECTIONS.find((s) => s.v === g.section)?.label || g.section}</td>
                <td className="px-4 py-3 text-zinc-700">{g.items_count} de {(g.items || []).length}</td>
                <td className="px-4 py-3 text-xs text-zinc-600">
                  {g.start_date && <div>desde {g.start_date.slice(0, 10)}</div>}
                  {g.end_date && <div>hasta {g.end_date.slice(0, 10)}</div>}
                  {!g.start_date && !g.end_date && "Sin vencimiento"}
                </td>
                <td className="px-4 py-3 numeric">{g.priority}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(g)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${g.live ? "bg-emerald-50 text-emerald-700" : g.active ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-500"}`}>
                    {g.live ? <><CheckCircle size={11} weight="fill" /> En vivo</> : g.active ? "Fuera de fechas" : <><XCircle size={11} weight="fill" /> Inactiva</>}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(g)} className="p-1.5 hover:bg-zinc-100 rounded"><PencilSimple size={14} weight="bold" /></button>
                  <button onClick={() => remove(g)} className="p-1.5 hover:bg-red-50 text-red-600 rounded ml-1"><Trash size={14} weight="bold" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editingId ? "Editar agrupación" : "Nueva agrupación"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Black Friday 2026" className="mt-1" data-testid="grouping-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Sección *</Label>
                <select value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="mt-1 w-full h-9 border border-zinc-300 rounded px-2 text-sm">
                  {SECTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 resize-none" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Color de acento</Label>
                <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="mt-1 w-full h-9 border border-zinc-300 rounded" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Prioridad</Label>
                <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 numeric" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Activa</Label>
                <div className="mt-1 h-9 flex items-center">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Desde</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Hasta</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50/40 border border-blue-200/60 rounded">
              <div>
                <Label className="text-xs uppercase tracking-wider text-blue-900">Rotación</Label>
                <select
                  value={form.rotation_strategy}
                  onChange={(e) => setForm({ ...form, rotation_strategy: e.target.value })}
                  className="mt-1 w-full h-9 border border-zinc-300 rounded px-2 text-sm bg-white"
                  data-testid="grouping-rotation-strategy"
                >
                  <option value="fixed">Orden fijo (manual)</option>
                  <option value="intelligent">Inteligente (menos vistos primero)</option>
                </select>
                <p className="text-[10px] text-zinc-500 mt-1">La rotación inteligente prioriza productos con menos impresiones para repartir la exposición.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-blue-900">Productos visibles</Label>
                <Input
                  type="number" min="0" value={form.visible_limit}
                  onChange={(e) => setForm({ ...form, visible_limit: e.target.value })}
                  placeholder="0 = todos"
                  className="mt-1 numeric"
                  data-testid="grouping-visible-limit"
                />
                <p className="text-[10px] text-zinc-500 mt-1">Cuántos elementos se muestran a la vez en la tienda. 0 = mostrar todos.</p>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1"><ImageIcon size={12} /> Banner (opcional)</Label>
              {form.banner_url ? (
                <div className="mt-2 relative inline-block">
                  <img src={form.banner_url} alt="" className="max-h-32 rounded border border-zinc-200" />
                  <button onClick={() => setForm({ ...form, banner_url: "" })} className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full"><X size={12} /></button>
                </div>
              ) : (
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => uploadBanner(e.target.files?.[0])} className="mt-2 text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#00873D] file:text-white file:rounded file:cursor-pointer" />
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Elementos *</Label>
              <div className="mt-1 border border-zinc-200 rounded p-3 bg-zinc-50 space-y-1">
                {form.items.length === 0 && <div className="text-xs text-zinc-400">Aún no agregas elementos</div>}
                {form.items.map((it, idx) => (
                  <div key={`${it.kind}-${it.id}`} className="flex items-center gap-2 bg-white p-2 rounded border border-zinc-200">
                    <span className="text-zinc-400 text-xs font-mono w-6">#{idx + 1}</span>
                    <span className="flex-1 text-xs">{itemLabel(it)}</span>
                    <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30 hover:bg-zinc-100 rounded"><ArrowUp size={12} /></button>
                    <button onClick={() => moveItem(idx, 1)} disabled={idx === form.items.length - 1} className="p-1 disabled:opacity-30 hover:bg-zinc-100 rounded"><ArrowDown size={12} /></button>
                    <button onClick={() => removeFormItem(idx)} className="text-zinc-400 hover:text-red-600"><X size={14} /></button>
                  </div>
                ))}
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto / promo / combo…" className="text-xs mt-2" />
                {filterText && (
                  <div className="max-h-60 overflow-y-auto bg-white rounded border border-zinc-200">
                    {filteredProducts.map((p) => (
                      <button key={`p-${p.id}`} type="button" onClick={() => addItem("product", p.id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-50 border-b border-zinc-100">🏷️ {p.name}</button>
                    ))}
                    {filteredPromotions.map((p) => (
                      <button key={`pr-${p.id}`} type="button" onClick={() => addItem("promotion", p.id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-50 border-b border-zinc-100">🔥 {p.name}</button>
                    ))}
                    {filteredCombos.map((c) => (
                      <button key={`c-${c.id}`} type="button" onClick={() => addItem("combo", c.id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-50 border-b border-zinc-100">📦 {c.name}</button>
                    ))}
                    {filteredProducts.length + filteredPromotions.length + filteredCombos.length === 0 && <div className="px-2 py-2 text-xs text-zinc-400">Sin resultados</div>}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#00873D] hover:bg-[#006A2D] text-white" data-testid="save-grouping">
              {saving ? "Guardando…" : editingId ? "Guardar" : "Crear agrupación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
